import currency from "currency.js";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { Alert } from "@/components/Alert/Alert";

import { extractDataFromFlightFare } from "./FlightFareCard/extractDataFromFlightFare";
import { FlightFareCard } from "./FlightFareCard/FlightFareCard";
import { FlightFareCardSkeleton } from "./FlightFareCard/FlightFareCardSkeleton";
import type { FlightFare, FlightFareLeg } from "./types";

type FlightFaresListProps = {
  fares: FlightFare[];
  getFareLeg: (fare: FlightFare) => FlightFareLeg | null;
  selectedFare: FlightFare | null;
  onFareSelect: (fare: FlightFare) => void;
  isLoading?: boolean;
  isError?: boolean;
  baselinePriceDifference?: number;
};

export function FlightFaresList(props: FlightFaresListProps) {
  const { fares, getFareLeg, selectedFare, isLoading, isError } = props;

  const intl = useIntl();

  const selectedFareLeg = selectedFare ? getFareLeg(selectedFare) : null;

  return (
    <div className="flex flex-col gap-4">
      {fares.map((fare) => {
        return (
          <FlightFaresListItem
            key={fare.code}
            fare={fare}
            selectedFareLeg={selectedFareLeg}
            {...props}
          />
        );
      })}

      {isLoading &&
        Array.from({ length: 3 }, (_, i) => <FlightFareCardSkeleton key={i} />)}

      {isError && (
        <Alert severity="info">
          {intl.formatMessage({
            defaultMessage:
              "We’re unable to show other fare types at this time. Try refreshing the page later",
            description: "Error message when failing to load flight fares",
          })}
        </Alert>
      )}
    </div>
  );
}

type FlightFaresListItemProps = FlightFaresListProps & {
  fare: FlightFare;
  selectedFareLeg: FlightFareLeg | null;
};

function FlightFaresListItem({
  fare,
  selectedFareLeg,
  getFareLeg,
  onFareSelect,
  baselinePriceDifference = 0,
}: FlightFaresListItemProps) {
  const { fareLeg, extractedData } = useMemo(() => {
    const fareLeg = getFareLeg(fare);

    if (!fareLeg) {
      return { fareLeg: null, extractedData: null };
    }

    const extractedData = extractDataFromFlightFare(fareLeg);

    return { fareLeg, extractedData };
  }, [fare, getFareLeg]);

  if (!fareLeg || !extractedData) return null;

  return (
    <FlightFareCard
      fare={extractedData}
      isSelected={fareLeg.brandCode === selectedFareLeg?.brandCode}
      onSelect={() => onFareSelect(fare)}
      priceDifference={
        currency(fare.priceDifference).subtract(baselinePriceDifference).value
      }
      currency={fare.currency}
    />
  );
}
